import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Spinner, Card, Row, Col, Badge, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faEdit, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';

const AnamneseTab = () => {
    const { currentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();
    
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    
    const [formData, setFormData] = useState({
        tipo_ficha: 'Medico',
        pergunta: '',
        tipo_pergunta: 'texto_livre',
        opcoes: []
    });

    const [newOption, setNewOption] = useState('');

    const fetchQuestions = useCallback(async () => {
        if (!currentEntidade) return;
        setIsLoading(true);
        try {
            const response = await axios.get(`/api/panamneses?entidade_id=${currentEntidade.id}`);
            setQuestions(response.data || []);
        } catch (error) {
            console.error("Erro ao buscar perguntas:", error);
            showAlertModal('Erro', 'Não foi possível carregar as perguntas de anamnese.', 'danger');
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade, showAlertModal]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleOpenModal = (question = null) => {
        if (question) {
            setCurrentQuestion(question);
            setFormData({
                tipo_ficha: question.tipo_ficha,
                pergunta: question.pergunta,
                tipo_pergunta: question.tipo_pergunta || 'texto_livre',
                opcoes: Array.isArray(question.opcoes) ? question.opcoes : []
            });
        } else {
            setCurrentQuestion(null);
            setFormData({
                tipo_ficha: 'Medico',
                pergunta: '',
                tipo_pergunta: 'texto_livre',
                opcoes: []
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setNewOption('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddOption = () => {
        if (newOption.trim()) {
            setFormData(prev => ({
                ...prev,
                opcoes: [...prev.opcoes, newOption.trim()]
            }));
            setNewOption('');
        }
    };

    const handleRemoveOption = (index) => {
        setFormData(prev => ({
            ...prev,
            opcoes: prev.opcoes.filter((_, i) => i !== index)
        }));
    };

    const handleSave = async () => {
        if (!formData.pergunta.trim()) {
            showAlertModal('Atenção', 'A pergunta não pode estar vazia.', 'warning');
            return;
        }

        if (formData.tipo_pergunta === 'multipla_escolha' && formData.opcoes.length === 0) {
            showAlertModal('Atenção', 'Múltipla escolha exige pelo menos uma opção.', 'warning');
            return;
        }

        setIsSaving(true);
        try {
            if (currentQuestion) {
                await axios.put(`/api/panamneses/${currentQuestion.id}`, formData);
                showAlertModal('Sucesso', 'Pergunta atualizada!', 'success');
            } else {
                await axios.post('/api/panamneses', {
                    ...formData,
                    entidade_id: currentEntidade.id
                });
                showAlertModal('Sucesso', 'Pergunta criada!', 'success');
            }
            handleCloseModal();
            fetchQuestions();
        } catch (error) {
            showAlertModal('Erro', 'Falha ao salvar a pergunta.', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Excluir esta pergunta? Isso não afetará anamneses já preenchidas.')) {
            try {
                await axios.delete(`/api/panamneses/${id}`);
                showAlertModal('Sucesso', 'Pergunta removida.', 'success');
                fetchQuestions();
            } catch (error) {
                showAlertModal('Erro', 'Não foi possível excluir a pergunta.', 'danger');
            }
        }
    };

    return (
        <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Perguntas de Anamnese</h5>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nova Pergunta
                </Button>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : (
                    <Table responsive hover className="align-middle">
                        <thead>
                            <tr>
                                <th>Ficha</th>
                                <th>Tipo</th>
                                <th>Pergunta</th>
                                <th className="text-center">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map(q => (
                                <tr key={q.id}>
                                    <td><Badge bg="info">{q.tipo_ficha}</Badge></td>
                                    <td><small className="text-muted">{q.tipo_pergunta}</small></td>
                                    <td>{q.pergunta}</td>
                                    <td className="text-center">
                                        <Button variant="link" className="text-primary p-0 me-3" onClick={() => handleOpenModal(q)}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Button>
                                        <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(q.id)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>

            <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{currentQuestion ? 'Editar Pergunta' : 'Nova Pergunta'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tipo de Ficha</Form.Label>
                                    <Form.Select name="tipo_ficha" value={formData.tipo_ficha} onChange={handleInputChange}>
                                        <option value="Medico">Médico</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tipo de Resposta Esperada</Form.Label>
                                    <Form.Select name="tipo_pergunta" value={formData.tipo_pergunta} onChange={handleInputChange}>
                                        <option value="texto_livre">Texto Livre</option>
                                        <option value="booleano">Sim / Não</option>
                                        <option value="multipla_escolha">Múltipla Escolha</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Enunciado da Pergunta</Form.Label>
                            <Form.Control as="textarea" rows={2} name="pergunta" value={formData.pergunta} onChange={handleInputChange} />
                        </Form.Group>

                        {formData.tipo_pergunta === 'multipla_escolha' && (
                            <div className="p-3 bg-light rounded border mb-3">
                                <Form.Label className="fw-bold">Opções do Menu</Form.Label>
                                <InputGroup className="mb-2">
                                    <Form.Control placeholder="Ex: Hipertenso" value={newOption} onChange={(e) => setNewOption(e.target.value)} />
                                    <Button variant="outline-secondary" onClick={handleAddOption}>+</Button>
                                </InputGroup>
                                <div className="d-flex flex-wrap gap-1">
                                    {formData.opcoes.map((opt, idx) => (
                                        <Badge key={idx} bg="white" text="dark" className="border">
                                            {opt} <FontAwesomeIcon icon={faTimes} className="ms-2 cursor-pointer text-danger" onClick={() => handleRemoveOption(idx)} />
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faSave} className="me-2" />}
                        Salvar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Card>
    );
};
export default AnamneseTab;
